import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import { IconButton } from '@web-config-app/core-react-ui';
import { StackLayout } from '@leagueplatform/genesis-core';
import { ArrayActionButtonsProps } from '../../types/components';
import { DeleteConfirmationModal } from '../delete-confirm-modal/delete-confirm-modal.component';
import { MoveToPositionModal } from '../move-to-position-modal/move-to-position-modal.component';

/**
 These are the Array Action buttons that control deleting and changing the position of the individual array items. 
 */

export const ArrayActionButtons = ({
  showDeleteModal = false,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onMoveToPosition,
  itemLabel,
  itemIndex,
  arrayLength,
  disableAllActions,
  dragHandleProps,
}: ArrayActionButtonsProps) => {
  const { formatMessage } = useIntl();

  const itemListPosition = itemIndex + 1;

  const lastItem = arrayLength === itemListPosition;
  const firstItem = itemIndex === 0;

  // If drag handle props are provided, show drag handle instead of move buttons
  // Only show drag handle if there's more than one item (no point dragging a single item)
  const showDragHandle = Boolean(dragHandleProps) && arrayLength > 1;
  const showMoveButtons = !showDragHandle;

  return (
    <StackLayout spacing="$quarter" orientation="horizontal">
      {showDragHandle && dragHandleProps && (
        <IconButton
          ref={dragHandleProps.ref}
          data-drag-handle-id={dragHandleProps.itemId}
          tooltip={formatMessage(
            { id: 'DRAG_TO_REORDER_TOOLTIP' },
            { item: itemLabel },
          )}
          label={formatMessage(
            { id: 'DRAG_TO_REORDER_SCREEN_READER' },
            { item: itemLabel },
          )}
          icon="moveUpAndDownIcon"
          size="small"
          priority="secondary"
          quiet
          disabled={disableAllActions}
          css={{
            cursor: disableAllActions ? 'not-allowed' : 'grab',
            '&:active': {
              cursor: disableAllActions ? 'not-allowed' : 'grabbing',
            },
          }}
          {...dragHandleProps.attributes}
          {...dragHandleProps.listeners}
        />
      )}
      {showMoveButtons && onMoveDown && (
        <IconButton
          tooltip={formatMessage(
            { id: 'MOVE_ITEM_DOWN_TOOLTIP' },
            { item: itemLabel },
          )}
          label={formatMessage({ id: 'DOWN' })}
          onClick={onMoveDown}
          disabled={lastItem || disableAllActions}
          icon="interfaceChevronDown"
          size="small"
          priority="tertiary"
          quiet
          css={{
            border: 'none',
            visibility: `${lastItem ? 'hidden' : 'visible'}`,
          }}
        />
      )}
      {showMoveButtons && onMoveUp && (
        <IconButton
          tooltip={formatMessage(
            { id: 'MOVE_ITEM_UP_TOOLTIP' },
            { item: itemLabel },
          )}
          label={formatMessage({ id: 'UP' })}
          onClick={onMoveUp}
          disabled={firstItem || disableAllActions}
          icon="interfaceChevronUp"
          size="small"
          priority="tertiary"
          quiet
          css={{
            border: 'none',
            visibility: `${firstItem ? 'hidden' : 'visible'}`,
          }}
        />
      )}
      {onDuplicate && (
        <IconButton
          tooltip={formatMessage(
            { id: 'DUPLICATE_ITEM_TOOLTIP' },
            { item: itemLabel },
          )}
          label={formatMessage({ id: 'DUPLICATE' })}
          onClick={onDuplicate}
          icon="interfaceCopy"
          size="small"
          priority="secondary"
          quiet
          disabled={disableAllActions}
        />
      )}
      {showMoveButtons && onMoveToPosition && arrayLength > 1 && (
        <MoveToPositionModal
          modalDescription={formatMessage(
            { id: 'MOVE_TO_POSITION_MODAL_DESCRIPTION' },
            { item: `"${itemLabel}"` },
          )}
          onMove={onMoveToPosition}
          currentIndex={itemIndex}
          arrayLength={arrayLength}
          itemLabel={itemLabel}
        >
          <IconButton
            tooltip={formatMessage(
              { id: 'MOVE_TO_POSITION_TOOLTIP' },
              { item: `"${itemLabel}"` },
            )}
            label={formatMessage({ id: 'MOVE_TO_POSITION' })}
            icon="moveUpAndDownIcon"
            size="small"
            priority="secondary"
            quiet
            disabled={disableAllActions}
          />
        </MoveToPositionModal>
      )}
      {showDeleteModal ? (
        <DeleteConfirmationModal
          modalDescription={formatMessage(
            { id: 'DELETE_ARRAY_ITEM_MODAL_DESCRIPTION' },
            { item: itemLabel },
          )}
          onDelete={onDelete}
        >
          <IconButton
            tooltip={formatMessage(
              { id: 'DELETE_ITEM_ARRAY_TOOLTIP' },
              { item: itemLabel },
            )}
            label={formatMessage({ id: 'DELETE' })}
            icon="interfaceDelete"
            size="small"
            priority="secondary"
            quiet
            destructive
            disabled={disableAllActions}
          />
        </DeleteConfirmationModal>
      ) : (
        <IconButton
          tooltip={formatMessage(
            { id: 'DELETE_ITEM_ARRAY_TOOLTIP' },
            { item: itemLabel },
          )}
          label={formatMessage({ id: 'DELETE' })}
          onClick={onDelete}
          icon="interfaceDelete"
          size="small"
          priority="secondary"
          quiet
          destructive
          disabled={disableAllActions}
        />
      )}
    </StackLayout>
  );
};
